import React, { useState, useEffect, useRef } from "react";

import { getToken } from "../api";
const authFetch = (url, opts = {}) => {
  const token = getToken();
  return fetch(url, { ...opts, headers: { ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
};

const BASE = window.location.hostname === "localhost"
  ? "http://localhost:8001"
  : `${window.location.protocol}//${window.location.host}/api`;

export default function Skills({ user }) {
  const [files, setFiles]           = useState([]);
  const [folders, setFolders]       = useState([]);
  const [currentFolder, setCurrentFolder] = useState("");
  const [uploading, setUploading]   = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]); // { name, status: pending|done|error }
  const [toast, setToast]           = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const fileRef  = useRef();
  const inputRef = useRef();

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const loadAll = async () => {
    try {
      const [fr, dr] = await Promise.all([
        authFetch(`${BASE}/skills/list`),
        authFetch(`${BASE}/skills/folders`)
      ]);
      const fd = await fr.json(); setFiles(Array.isArray(fd) ? fd : []);
      const dd = await dr.json(); setFolders(Array.isArray(dd) ? dd : []);
    } catch { showToast("Error cargando datos"); }
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (showNewFolder) setTimeout(() => inputRef.current?.focus(), 50); }, [showNewFolder]);

  // ── Subida múltiple ──────────────────────────────────────────
  const upload = async (e) => {
    const selected = Array.from(e.target.files);
    if (!selected.length) return;

    const queue = selected.map(f => ({ name: f.name, status: "pending" }));
    setUploadQueue(queue);
    setUploading(true);

    for (let i = 0; i < selected.length; i++) {
      const file = selected[i];
      const fd = new FormData();
      fd.append("file", file);
      fd.append("uploaded_by", user?.name || "Desconocido");
      fd.append("folder", currentFolder);
      try {
        const r = await authFetch(`${BASE}/skills/upload`, { method: "POST", body: fd });
        if (!r.ok) throw new Error();
        setUploadQueue(q => q.map((item, idx) => idx === i ? { ...item, status: "done" } : item));
      } catch {
        setUploadQueue(q => q.map((item, idx) => idx === i ? { ...item, status: "error" } : item));
      }
    }

    await loadAll();
    setUploading(false);
    const ok = queue.filter((_, i) => true).length; // se actualiza via estado
    showToast(`${selected.length === 1 ? "Archivo subido" : `${selected.length} archivos subidos`} ✓`);
    setTimeout(() => setUploadQueue([]), 2500);
    fileRef.current.value = "";
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const r = await authFetch(`${BASE}/skills/folder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, parent: currentFolder })
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail); }
      showToast(`Carpeta "${name}" creada ✓`);
      setNewFolderName(""); setShowNewFolder(false);
      loadAll();
    } catch (e) { showToast(e.message || "Error al crear carpeta"); }
  };

  const deleteFile = async (id) => {
    try {
      await authFetch(`${BASE}/skills/${id}`, { method: "DELETE" });
      showToast("Eliminado");
      loadAll();
    } catch { showToast("Error al eliminar"); }
  };

  const deleteFolder = async (folder) => {
    try {
      await authFetch(`${BASE}/skills/folder`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: folder.path })
      });
      showToast("Carpeta eliminada");
      loadAll();
    } catch { showToast("Error al eliminar carpeta"); }
  };

  const visibleFolders = folders.filter(f => f.parent === currentFolder);
  const visibleFiles   = files.filter(f => (f.folder || "") === currentFolder);

  const breadcrumb = () => {
    if (!currentFolder) return [{ label: "Inicio", path: "" }];
    const parts = currentFolder.split("/");
    const crumbs = [{ label: "Inicio", path: "" }];
    parts.forEach((p, i) => {
      crumbs.push({ label: p, path: parts.slice(0, i + 1).join("/") });
    });
    return crumbs;
  };

  const fmt     = (iso) => new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  const fmtSize = (b)   => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;

  const S = {
    wrap: { padding: "0", fontFamily: "Syne, sans-serif" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem", borderBottom: "0.5px solid var(--border)", paddingBottom: "1.5rem", flexWrap: "wrap", gap: 12 },
    title: { fontSize: 26, fontWeight: 700, color: "var(--text)", fontFamily: "Syne, sans-serif", margin: 0, letterSpacing: "-0.5px" },
    sub: { fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 8, fontFamily: "Syne, sans-serif" },
    actions: { display: "flex", gap: 8, alignItems: "center" },
    btn: { padding: "8px 12px", background: "var(--gold)", color: "var(--bgApp)", border: "none", fontFamily: "Syne, sans-serif", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", fontWeight: 700, borderRadius: "5px" },
    btnOutline: { padding: "8px 12px", background: "none", color: "var(--gold)", border: "1px solid var(--goldDim)", fontFamily: "Syne, sans-serif", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", fontWeight: 600, borderRadius: "5px" },
    breadcrumb: { display: "flex", alignItems: "center", gap: 6, marginBottom: "1.5rem", fontSize: 11 },
    crumbLink: { color: "var(--gold)", cursor: "pointer", fontFamily: "Syne, sans-serif", letterSpacing: "0.08em", textTransform: "uppercase", fontSize: 10 },
    crumbSep: { color: "var(--mute)" },
    crumbCurrent: { color: "var(--text)", fontFamily: "Syne, sans-serif", letterSpacing: "0.08em", textTransform: "uppercase", fontSize: 10 },
    newFolderRow: { display: "flex", gap: 8, alignItems: "center", marginBottom: "1.5rem" },
    input: { background: "var(--lift)", border: "0.5px solid var(--border)", color: "var(--text)", padding: "9px 14px", fontFamily: "Syne, sans-serif", fontSize: 13, outline: "none", flex: 1, maxWidth: 260, borderRadius: "6px" },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 10, marginBottom: "2rem" },
    folderCard: { background: "var(--card)", border: "0.5px solid var(--border)", borderRadius: "8px", padding: "1.2rem 1rem", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, transition: "border-color .15s" },
    folderIcon: { fontSize: 28 },
    folderName: { color: "var(--text)", fontSize: 12, fontFamily: "Syne, sans-serif", letterSpacing: "0.05em", textAlign: "center", wordBreak: "break-word" },
    table: { width: "100%", borderCollapse: "collapse" },
    th: { textAlign: "left", fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--mute)", padding: "8px 12px", borderBottom: "0.5px solid var(--border)", fontFamily: "Syne, sans-serif" },
    td: { padding: "12px", borderBottom: "0.5px solid var(--border)", color: "var(--text)", fontSize: 13 },
    iconBtn: { background: "none", border: "none", cursor: "pointer", color: "var(--gold)", fontSize: 16, marginLeft: 8 },
    empty: { textAlign: "center", padding: "3rem", color: "var(--mute)", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase" },
    toast: { position: "fixed", bottom: 24, right: 24, background: "var(--card)", border: "1px solid var(--goldDim)", color: "var(--gold)", padding: "10px 20px", fontFamily: "Syne, sans-serif", fontSize: 12, fontWeight: 700, zIndex: 999, borderRadius: "6px" },
    sectionLabel: { fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--mute)", marginBottom: 10, fontFamily: "Syne, sans-serif" },
  };

  const crumbs = breadcrumb();

  return (
    <div style={S.wrap}>
      {toast && <div style={S.toast}>{toast}</div>}
      <div style={S.header}>
        <div>
          <div style={S.sub}>Recursos internos</div>
          <h1 style={S.title}>Base de Conocimiento</h1>
        </div>
        <div style={S.actions}>
          <button style={S.btnOutline} onClick={() => setShowNewFolder(v => !v)}>+ Nueva carpeta</button>
          <input ref={fileRef} type="file" multiple style={{ display: "none" }} onChange={upload} />
          <button style={S.btn} onClick={() => fileRef.current.click()} disabled={uploading}>
            {uploading ? "Subiendo..." : "+ Subir archivos"}
          </button>
        </div>
      </div>

      {/* Cola de subida */}
      {uploadQueue.length > 0 && (
        <div style={{ background: "var(--card)", border: "0.5px solid var(--border)", borderRadius: 8,
          padding: "12px 16px", marginBottom: "1.5rem", display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ ...S.sectionLabel, marginBottom: 4 }}>Subiendo archivos...</div>
          {uploadQueue.map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12,
              fontFamily: "Syne, sans-serif", color: "var(--mute)" }}>
              <span style={{ fontSize: 14, flexShrink: 0 }}>
                {item.status === "pending" ? "⏳" : item.status === "done" ? "✅" : "❌"}
              </span>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {item.name}
              </span>
              <span style={{ color: item.status === "error" ? "#c0392b" : "var(--mute)", flexShrink: 0 }}>
                {item.status === "pending" ? "Subiendo..." : item.status === "done" ? "Listo" : "Error"}
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={S.breadcrumb}>
        {crumbs.map((c, i) => (
          <React.Fragment key={c.path}>
            {i < crumbs.length - 1
              ? <span style={S.crumbLink} onClick={() => setCurrentFolder(c.path)}>{c.label}</span>
              : <span style={S.crumbCurrent}>{c.label}</span>
            }
            {i < crumbs.length - 1 && <span style={S.crumbSep}>›</span>}
          </React.Fragment>
        ))}
      </div>

      {showNewFolder && (
        <div style={S.newFolderRow}>
          <input
            ref={inputRef}
            style={S.input}
            placeholder="Nombre de la carpeta..."
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") createFolder(); if (e.key === "Escape") { setShowNewFolder(false); setNewFolderName(""); } }}
          />
          <button style={S.btn} onClick={createFolder}>Crear</button>
          <button style={{ ...S.iconBtn, fontSize: 18, color: "var(--mute)" }} onClick={() => { setShowNewFolder(false); setNewFolderName(""); }}>✕</button>
        </div>
      )}

      {visibleFolders.length > 0 && (
        <div style={{ marginBottom: "2rem" }}>
          <div style={S.sectionLabel}>Carpetas</div>
          <div style={S.grid}>
            {visibleFolders.map(f => (
              <div key={f.id} style={{ ...S.folderCard, position: "relative" }}
                onClick={() => setCurrentFolder(f.path)}
                onMouseEnter={e => e.currentTarget.style.borderColor = "var(--gold)"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}>
                <button
                  style={{ position: "absolute", top: 4, right: 4, background: "none", border: "none", color: "#c0392b", cursor: "pointer", fontSize: 13, lineHeight: 1, padding: "2px 5px" }}
                  onClick={e => { e.stopPropagation(); deleteFolder(f); }}
                  title="Eliminar carpeta">✕</button>
                <span style={S.folderIcon}>📁</span>
                <span style={S.folderName}>{f.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {visibleFiles.length === 0 && visibleFolders.length === 0 ? (
        <div style={S.empty}>📂 Vacío — Crea una carpeta o sube archivos aquí</div>
      ) : visibleFiles.length > 0 && (
        <div>
          {visibleFolders.length > 0 && <div style={S.sectionLabel}>Archivos</div>}
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Nombre</th>
                <th style={S.th}>Tamaño</th>
                <th style={S.th}>Subido por</th>
                <th style={S.th}>Fecha</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {visibleFiles.map(f => (
                <tr key={f.id}>
                  <td style={S.td}>{f.filename}</td>
                  <td style={{ ...S.td, color: "var(--mute)" }}>{fmtSize(f.size)}</td>
                  <td style={{ ...S.td, color: "var(--mute)" }}>{f.uploaded_by}</td>
                  <td style={{ ...S.td, color: "var(--mute)" }}>{fmt(f.uploaded_at)}</td>
                  <td style={S.td}>
                    <button style={S.iconBtn} onClick={() => window.open(`${BASE}/skills/download/${f.id}`, "_blank")} title="Descargar">⬇</button>
                    <button style={{ ...S.iconBtn, color: "#c0392b" }} onClick={() => deleteFile(f.id)} title="Eliminar">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
